import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown, ArrowRight, ArrowUpRight, BarChart3, Check, ChevronDown,
  Globe2, Layers3, Mail, MapPin, MessageCircle, MessageSquareReply,
  ShieldCheck, Sparkles, Star, TrendingUp, WandSparkles, Workflow
} from 'lucide-react';
import './HomePage.css';

const WHATSAPP_URL = 'https://wa.me/919939350820?text=Hi%2C%20I%27d%20like%20to%20know%20more%20about%20CredBloom.';
const EMAIL_URL = 'mailto:support@itsmmp.com?subject=CredBloom%20Demo%20Enquiry';

const chapters = [
  {
    number: '01',
    eyebrow: 'LISTEN',
    title: <>A single review can <em>start something big.</em></>,
    description: 'Every star, question and customer comment is a chance to build trust. Bring your feedback together instead of chasing it across tabs.',
    detail: 'Review monitoring · Business profiles · One clear workspace'
  },
  {
    number: '02',
    eyebrow: 'CONNECT',
    title: <>Turn feedback into <em>real conversations.</em></>,
    description: 'Thoughtful replies tell customers you are listening. Use AI-assisted drafts, your brand voice and controlled reply workflows to respond with confidence.',
    detail: 'AI-assisted replies · Team workflows · Response controls'
  },
  {
    number: '03',
    eyebrow: 'UNDERSTAND',
    title: <>Find the signal <em>behind the stars.</em></>,
    description: 'Look beyond individual ratings. Understand sentiment, review patterns, business performance and local competitive context in one place.',
    detail: 'Sentiment · Reputation trends · Business insights'
  },
  {
    number: '04',
    eyebrow: 'BLOOM',
    title: <>Good experiences deserve <em>to be seen.</em></>,
    description: 'Make it easier to collect new feedback, showcase authentic customer voices and build a stronger digital presence—one interaction at a time.',
    detail: 'Review links · QR journeys · Website widgets'
  }
];

const capabilities = [
  { icon: Star, index: '01', title: 'Reviews, together', text: 'Monitor customer feedback and manage review conversations from a connected workspace.' },
  { icon: MessageSquareReply, index: '02', title: 'Replies with intelligence', text: 'Create AI-assisted responses that still sound like your business, with control over publishing.' },
  { icon: MapPin, index: '03', title: 'Google Business Profile', text: 'Connect authorised locations to sync supported reviews, insights and business interactions.' },
  { icon: BarChart3, index: '04', title: 'Insights that make sense', text: 'Explore sentiment, trends and local competitive context without drowning in data.' },
  { icon: Workflow, index: '05', title: 'Teams in sync', text: 'Organise branches, users and response workflows as your operations grow.' },
  { icon: Globe2, index: '06', title: 'Trust, everywhere', text: 'Collect reviews with shareable links and QR flows, then showcase them with website widgets.' }
];

function Brand({ light = false }) {
  return (
    <Link to="/" className={'cb-brand' + (light ? ' cb-brand-light' : '')} aria-label="CredBloom — homepage">
      <span className="cb-brand-icon" aria-hidden="true"><span>C</span><span>B</span></span>
      <span className="cb-brand-name"><span>Cred</span><span>Bloom</span></span>
    </Link>
  );
}

function ContactActions({ dark = false, compact = false }) {
  return (
    <div className={'cb-actions' + (dark ? ' cb-actions-dark' : '') + (compact ? ' cb-actions-compact' : '')}>
      <a className="cb-btn cb-btn-green" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
        <MessageCircle size={18} strokeWidth={2.1} />
        Let's talk on WhatsApp
        <ArrowUpRight size={16} />
      </a>
      <a className="cb-btn cb-btn-outline" href={EMAIL_URL}>
        <Mail size={17} />
        Email us
      </a>
    </div>
  );
}

function FloatingReview({ className, name, line, stars = 5 }) {
  return (
    <div className={'cb-review-card ' + className}>
      <div className="cb-review-card-top"><span className="cb-review-avatar">{name.charAt(0)}</span><span className="cb-review-name">{name}</span><span className="cb-review-stars" aria-label={stars + ' stars'}>{'★'.repeat(stars)}</span></div>
      <span className="cb-review-line">{line}</span>
    </div>
  );
}

function TrustSculpture({ variant = 'hero', stage = 0 }) {
  return (
    <div className={'cb-sculpture cb-sculpture-' + variant} data-art-stage={stage} aria-hidden="true">
      <div className="cb-sculpture-glow" />
      <div className="cb-sculpture-grid" />
      <div className="cb-orbit cb-orbit-one" />
      <div className="cb-orbit cb-orbit-two" />
      <div className="cb-orbit cb-orbit-three" />
      <div className="cb-orb-shadow" />
      <div className="cb-orb">
        <div className="cb-orb-sheen" />
        <div className="cb-orb-inner"><span className="cb-orb-star">✳</span></div>
      </div>

      <div className="cb-bloom">
        <div className="cb-petal cb-petal-1" /><div className="cb-petal cb-petal-2" />
        <div className="cb-petal cb-petal-3" /><div className="cb-petal cb-petal-4" />
        <div className="cb-petal cb-petal-5" /><div className="cb-petal cb-petal-6" />
        <div className="cb-petal cb-petal-7" /><div className="cb-petal cb-petal-8" />
        <div className="cb-bloom-core">CB</div>
      </div>

      <FloatingReview className="cb-review-one" name="Maya" line="Such a wonderful experience." />
      <FloatingReview className="cb-review-two" name="Arjun" line="Really thoughtful service." />
      <FloatingReview className="cb-review-three" name="Ava" line="Would recommend!" />
      <div className="cb-signal-pill cb-signal-one"><Star size={13} fill="currentColor" /> Customer voice</div>
      <div className="cb-signal-pill cb-signal-two"><Sparkles size={14} /> Every review matters</div>

      <div className="cb-reply-card">
        <div className="cb-reply-label"><Sparkles size={14} /> AI-ASSISTED REPLY</div>
        <p>Thank you for sharing your experience! We're glad you enjoyed your visit.</p>
        <div className="cb-reply-footer"><span><Check size={13} /> Ready for review</span><span className="cb-reply-send"><ArrowUpRight size={13} /></span></div>
      </div>

      <div className="cb-insights-card">
        <div className="cb-insights-caption"><TrendingUp size={15} /> REPUTATION PULSE</div>
        <div className="cb-insights-title">Every signal tells a story.</div>
        <svg viewBox="0 0 260 98" role="presentation" focusable="false">
          <defs><linearGradient id={'cb-chart-fill-' + variant} x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#7ED957" stopOpacity=".24"/><stop offset="1" stopColor="#7ED957" stopOpacity="0"/></linearGradient></defs>
          <path d="M0 76 C20 65 31 84 50 62 S92 68 117 42 S155 58 180 26 S219 36 260 8 L260 98 L0 98Z" fill={'url(#cb-chart-fill-' + variant + ')'} />
          <path d="M0 76 C20 65 31 84 50 62 S92 68 117 42 S155 58 180 26 S219 36 260 8" stroke="#7ED957" strokeWidth="3" fill="none" strokeLinecap="round"/>
          <circle cx="260" cy="8" r="5" fill="#7ED957" />
        </svg>
        <div className="cb-insights-footer"><span>Sentiment</span><span>Trends</span><span>Insights</span></div>
      </div>

      <div className="cb-bloom-caption"><span className="cb-caption-spark">✦</span> Better conversations. Stronger trust.</div>
    </div>
  );
}

function Journey() {
  const journeyRef = useRef(null);
  const [stage, setStage] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const setPreference = () => setReducedMotion(media.matches);
    setPreference();
    if (media.addEventListener) media.addEventListener('change', setPreference);
    else media.addListener(setPreference);

    // Never intercept wheel, swipe, keyboard, or native browser scrolling.
    // Keeping scrolling fully native prevents the last-chapter exit lock
    // and makes navigation equally responsive in either direction.
    let frame = null;
    const update = () => {
      frame = null;
      if (!journeyRef.current) return;
      const bounds = journeyRef.current.getBoundingClientRect();
      const scrollable = Math.max(1, bounds.height - window.innerHeight);
      const progress = Math.max(0, Math.min(1, -bounds.top / scrollable));
      journeyRef.current.style.setProperty('--cb-scroll', progress.toFixed(4));

      if (!media.matches) {
        const next = Math.min(chapters.length - 1, Math.floor(progress * chapters.length));
        setStage(current => current === next ? current : next);
      }
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (media.removeEventListener) media.removeEventListener('change', setPreference);
      else media.removeListener(setPreference);
    };
  }, []);

  return (
    <section className="cb-journey" id="journey" ref={journeyRef} data-stage={stage} aria-label="The CredBloom journey">
      <div className="cb-journey-sticky">
        <div className="cb-journey-topline"><span>THE CRED/BLOOM EFFECT</span><span>YOUR REPUTATION, IN MOTION</span></div>
        <div className="cb-journey-content">
          <div className="cb-story-copy">
            <div className="cb-journey-count"><span>THE JOURNEY</span><strong>0{stage + 1} <i>/</i> 04</strong></div>
            {chapters.map((chapter, index) => (
              <div
                className={'cb-chapter' + (stage === index ? ' cb-chapter-active' : '')}
                key={chapter.number}
                aria-hidden={!reducedMotion && stage !== index}
              >
                <span className="cb-chapter-kicker"><span className="cb-kicker-line" /> {chapter.eyebrow}</span>
                <h2>{chapter.title}</h2>
                <p>{chapter.description}</p>
                <div className="cb-chapter-detail"><Check size={16} /> {chapter.detail}</div>
              </div>
            ))}
            <div className="cb-journey-steps" aria-hidden="true">
              {chapters.map((chapter, index) => <span key={chapter.number} className={index === stage ? 'is-current' : ''} />)}
            </div>
          </div>
          <div className="cb-journey-art"><TrustSculpture variant="story" stage={stage} /></div>
        </div>
        <div className="cb-journey-bottomline"><span>SCROLL TO FOLLOW THE STORY <ArrowDown size={13} /></span><span>© CREDBLOOM</span></div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="cb-page">
      <a className="cb-skip-link" href="#main-content">Skip to content</a>
      <header className="cb-header">
        <div className="cb-header-inner">
          <Brand light />
          <nav className="cb-nav-links" aria-label="Primary navigation">
            <a href="#journey">The story</a>
            <a href="#capabilities">What we do</a>
            <a href="#google-access">Google access</a>
          </nav>
          <div className="cb-header-right">
            <Link className="cb-signin" to="/login">Sign in</Link>
            <a className="cb-header-contact" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">Let's connect <ArrowUpRight size={15}/></a>
          </div>
        </div>
      </header>

      <main id="main-content">
        <section className="cb-hero" aria-labelledby="cb-hero-title">
          <div className="cb-hero-grain" aria-hidden="true" />
          <div className="cb-hero-inner">
            <div className="cb-hero-copy">
              <p className="cb-eyebrow"><span className="cb-green-dot" /> INTRODUCING CREDBLOOM <span className="cb-eyebrow-line" /></p>
              <h1 id="cb-hero-title">Your reputation<br/>is ready to <span>bloom<span className="cb-period">.</span></span></h1>
              <p className="cb-hero-subtitle">Make every review count. Turn customer conversations into lasting trust—with one intelligent reputation workspace.</p>
              <ContactActions dark />
              <div className="cb-hero-trustline"><span className="cb-trustline-icon"><Check size={15}/></span> Built for real businesses, real reviews and real connections.</div>
            </div>
            <div className="cb-hero-art"><TrustSculpture /></div>
          </div>
          <div className="cb-hero-bottom">
            <a href="#journey" className="cb-scroll-link"><span className="cb-scroll-symbol"><ChevronDown size={19}/></span><span>SCROLL TO SEE THE STORY</span></a>
            <span className="cb-hero-side-note">REPUTATION MANAGEMENT, REIMAGINED — 01/04</span>
          </div>
        </section>

        <div className="cb-marquee" aria-label="Listen. Connect. Understand. Bloom.">
          <div className="cb-marquee-track" aria-hidden="true">
            {Array.from({ length: 4 }, (_, index) => (
              <span key={index}>LISTEN <span>✳</span> CONNECT <span>✳</span> UNDERSTAND <span>✳</span> BLOOM <span>✳</span></span>
            ))}
          </div>
        </div>

        <Journey />

        <section className="cb-platform" id="capabilities">
          <div className="cb-section-inner">
            <div className="cb-section-intro">
              <div><span className="cb-small-label">THE PLATFORM / 06 WAYS TO GROW</span><h2>Everything your reputation<br/><em>needs to thrive.</em></h2></div>
              <p>Less switching between tools. More room to focus on the relationships that make your business worth choosing.</p>
            </div>
            <div className="cb-capabilities-grid">
              {capabilities.map(({ icon: Icon, index, title, text }) => (
                <article className="cb-feature" key={index}>
                  <div className="cb-feature-top"><span>{index} / 06</span><ArrowUpRight size={18}/></div>
                  <div className="cb-feature-icon"><Icon size={31} strokeWidth={1.6}/></div>
                  <div><h3>{title}</h3><p>{text}</p></div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="cb-showcase">
          <div className="cb-showcase-inner">
            <div className="cb-showcase-visual" aria-hidden="true">
              <div className="cb-dashboard-back"><span className="cb-dashboard-dots">● ● ●</span><div className="cb-dashboard-bars"><i/><i/><i/><i/><i/><i/><i/></div></div>
              <div className="cb-dashboard-front">
                <div className="cb-dashboard-label"><span className="cb-green-dot"/> YOUR REPUTATION WORKSPACE</div>
                <div className="cb-dashboard-title">Clarity feels good.</div>
                <div className="cb-dashboard-metric"><span><Star fill="currentColor" size={18}/> Customer feedback</span><Check size={18}/></div>
                <div className="cb-dashboard-metric"><span><MessageSquareReply size={18}/> Reply workflows</span><Check size={18}/></div>
                <div className="cb-dashboard-metric"><span><TrendingUp size={18}/> Reputation insights</span><Check size={18}/></div>
              </div>
              <div className="cb-dashboard-bubble"><Sparkles size={18}/> All connected</div>
            </div>
            <div className="cb-showcase-copy">
              <span className="cb-small-label">MADE FOR THE PEOPLE BEHIND THE BRAND</span>
              <h2>From one location to <em>everywhere you're going.</em></h2>
              <p>CredBloom brings multi-location review workflows, team visibility and flexible brand experiences into one home. A thoughtful fit for growing businesses and the partners who support them.</p>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="cb-text-link">Let's explore your use case <ArrowUpRight size={19}/></a>
            </div>
          </div>
        </section>

        <section className="cb-google-section" id="google-access">
          <div className="cb-google-inner">
            <div className="cb-google-mark" aria-hidden="true"><ShieldCheck size={44} strokeWidth={1.25}/></div>
            <div>
              <span className="cb-small-label">CONTROL & TRANSPARENCY</span>
              <h2>Your business profile.<br/>Your permission. <em>Your control.</em></h2>
              <p>CredBloom uses Google OAuth to let business administrators authorise access to supported Google Business Profile features. You choose when to connect an account, and we use that access to provide the reputation tools described in our <Link to="/pp">Privacy Policy</Link>.</p>
              <div className="cb-google-points"><span><Check size={15}/> Permission-based connection</span><span><Check size={15}/> Authorised business locations</span><span><Check size={15}/> Clear data practices</span></div>
            </div>
            <div className="cb-google-links"><Link to="/pp">Privacy Policy <ArrowUpRight size={15}/></Link><Link to="/tandc">Terms & Conditions <ArrowUpRight size={15}/></Link></div>
          </div>
        </section>

        <section className="cb-final" id="contact">
          <div className="cb-final-glow" aria-hidden="true" />
          <div className="cb-final-inner">
            <span className="cb-small-label"><span className="cb-green-dot" /> YOUR NEXT CHAPTER STARTS HERE</span>
            <h2>Let's make your<br/>reputation <em>bloom.</em></h2>
            <p>Tell us about your business. We'll show you how CredBloom can help turn feedback into conversations and trust into momentum.</p>
            <ContactActions dark />
            <p className="cb-contact-note">Prefer a direct message? <a href="mailto:support@itsmmp.com">support@itsmmp.com</a> · <a href="https://wa.me/919939350820" target="_blank" rel="noopener noreferrer">+91 99393 50820</a></p>
          </div>
          <div className="cb-final-decor" aria-hidden="true">✳</div>
        </section>
      </main>

      <footer className="cb-footer">
        <div className="cb-footer-top">
          <div><Brand light/><p>Better conversations.<br/>Stronger trust.</p></div>
          <div className="cb-footer-links">
            <div><span>EXPLORE</span><a href="#journey">Our story</a><a href="#capabilities">Platform features</a><Link to="/login">Client sign in</Link></div>
            <div><span>CONNECT</span><a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">WhatsApp</a><a href="mailto:support@itsmmp.com">support@itsmmp.com</a></div>
            <div><span>LEGAL</span><Link to="/pp">Privacy Policy</Link><Link to="/tandc">Terms & Conditions</Link></div>
          </div>
        </div>
        <div className="cb-footer-bottom"><span>© {new Date().getFullYear()} CredBloom. All rights reserved.</span><span>Made to help great reputations grow. <span className="cb-footer-heart">✳</span></span></div>
      </footer>
    </div>
  );
}
